import { CheckIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import { cn } from "@/core/utils/utils";
import { STAGE_LABELS } from "@/payroll/editor/enrich-editor";
import type { TurnStep } from "@/payroll/editor/types";

const stageLabel = (step: TurnStep) =>
  STAGE_LABELS[step.stage as keyof typeof STAGE_LABELS] ?? step.stage;

/**
 * What the assistant is doing right now, with what it has already done folded
 * away behind it.
 *
 * Every step used to hold its own line, and the line led with the stage. Over a
 * ten minute request that stacked seven rows of "Reading your payroll
 * information" and "Making the change", each one cutting off the only part that
 * said anything: which rate, whose plan, which sheet. The verb is the same
 * every time. The detail is the request.
 *
 * So the detail leads, the stage trails it quietly, and only the current step
 * is shown. Opening the row gives the detail in full and the steps before it,
 * newest first.
 *
 * Stages, never tool names. `apply_script_edit_batch` is not something an owner
 * asked for, and a count is the honest way to say a stretch was long: "Reading
 * your payroll information ×11" is a fact about their request, where eleven
 * separate rows would be a fact about ours.
 */
export const TurnActivity = ({ steps }: { steps: TurnStep[] }) => {
  const [open, setOpen] = useState(false);
  if (!steps.length) return null;

  const active = steps[steps.length - 1];
  const earlier = steps.slice(0, -1).reverse();
  const label = stageLabel(active);
  const count = active.count && active.count > 1 ? `×${active.count}` : "";

  return (
    <div className="flex flex-col gap-1 text-sm">
      <div className="flex items-start gap-2">
        <span
          aria-hidden
          // One line box tall, so the dot centres on the first line of the
          // detail whatever the type scale does. A fixed 18px here put it
          // three pixels below the words it marks.
          className="flex h-[1lh] w-3.5 shrink-0 items-center justify-center"
        >
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-stroke-interactive" />
        </span>
        <div className="min-w-0 flex-1">
          <p
            className={cn("text-secondary-text", { truncate: !open })}
            aria-live="polite"
          >
            {active.detail || label}
          </p>
          {/* The stage only earns a second line once the detail has taken the
              first one. Before a plan exists there is no detail, and repeating
              the stage under itself would say it twice. */}
          {(!!active.detail || !!count) && (
            <p className="text-tertiary-text">
              {active.detail ? label : ""}
              {active.detail && count ? " " : ""}
              {count}
            </p>
          )}
        </div>
        {earlier.length > 0 && (
          <button
            type="button"
            onClick={() => setOpen(!open)}
            aria-expanded={open}
            // One line box, like the dot opposite it, so the mark, the words
            // and this control all centre on the same line.
            className="flex h-[1lh] shrink-0 items-center gap-1 text-tertiary-text hover:text-secondary-text"
          >
            {open ? "Hide" : `${earlier.length} before this`}
            <ChevronDownIcon
              aria-hidden
              className={cn("h-3 w-3 transition-transform", {
                "rotate-180": open,
              })}
            />
          </button>
        )}
      </div>
      {open && earlier.length > 0 && (
        <ol className="flex flex-col gap-1 pl-[22px]">
          {earlier.map((step, index) => (
            <li
              key={`${step.stage}-${earlier.length - index}`}
              className="flex items-start gap-2 text-tertiary-text"
            >
              <CheckIcon
                aria-hidden
                className="mt-[3px] h-3 w-3 shrink-0 text-green-60"
              />
              {/* The detail usually ends in a full stop, so the stage needs a
                  mark of its own to sit after it. Without one the two ran
                  together as "…as 17%. Making the change". */}
              <span className="min-w-0 flex-1">
                {step.detail || stageLabel(step)}
                {!!step.detail && (
                  <span className="text-quaternary-text">
                    {" · "}
                    {stageLabel(step)}
                  </span>
                )}
                {!!step.count && step.count > 1 && ` ×${step.count}`}
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};
